import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import clsx from 'clsx';
import { useTutorial } from './TutorialContext';

const PADDING = 8;
const CARD_GAP = 12;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getCardStyle = (targetRect, placement) => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const cardWidth = Math.min(360, viewportWidth - 32);
  const cardHeight = 220;

  if (!targetRect || placement === 'center') {
    return {
      top: clamp((viewportHeight - cardHeight) / 2, 16, viewportHeight - cardHeight - 16),
      left: clamp((viewportWidth - cardWidth) / 2, 16, viewportWidth - cardWidth - 16),
      width: cardWidth,
    };
  }

  let top = targetRect.bottom + CARD_GAP;
  let left = targetRect.left;

  if (placement === 'top') {
    top = targetRect.top - cardHeight - CARD_GAP;
  } else if (placement === 'left') {
    top = targetRect.top;
    left = targetRect.left - cardWidth - CARD_GAP;
  } else if (placement === 'right') {
    top = targetRect.top;
    left = targetRect.right + CARD_GAP;
  } else if (placement === 'bottom') {
    top = targetRect.bottom + CARD_GAP;
    left = targetRect.left;
  }

  return {
    top: clamp(top, 16, viewportHeight - cardHeight - 16),
    left: clamp(left, 16, viewportWidth - cardWidth - 16),
    width: cardWidth,
  };
};

export default function TutorialOverlay() {
  const {
    isRunning,
    overlayReady,
    activeChapter,
    activeStepIndex,
    activeStep,
    next,
    back,
    exitTour,
  } = useTutorial();

  const [targetRect, setTargetRect] = useState(null);

  const measureTarget = useCallback(() => {
    if (!activeStep?.targetSelector) {
      setTargetRect(null);
      return;
    }

    const element = document.querySelector(activeStep.targetSelector);
    if (!element) {
      setTargetRect(null);
      return;
    }

    element.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });

    window.setTimeout(() => {
      const rect = element.getBoundingClientRect();
      setTargetRect({
        top: rect.top - PADDING,
        left: rect.left - PADDING,
        width: rect.width + PADDING * 2,
        height: rect.height + PADDING * 2,
        bottom: rect.bottom + PADDING,
        right: rect.right + PADDING,
      });
    }, 150);
  }, [activeStep]);

  useLayoutEffect(() => {
    if (!isRunning || !overlayReady) {
      setTargetRect(null);
      return undefined;
    }

    measureTarget();

    const onResize = () => measureTarget();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [isRunning, overlayReady, measureTarget, activeStepIndex]);

  useEffect(() => {
    if (!isRunning) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        exitTour();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isRunning, exitTour]);

  if (!isRunning || !activeChapter || !activeStep) return null;

  const totalSteps = activeChapter.steps.length;
  const isFirstStep = activeStepIndex === 0;
  const isLastStep = activeStepIndex === totalSteps - 1;
  const cardStyle = getCardStyle(targetRect, activeStep.placement || 'bottom');

  return (
    <div className="fixed inset-0 z-[110]" aria-live="polite">
      {targetRect ? (
        <div
          className="absolute rounded-md ring-2 ring-primary-500 ring-offset-2 ring-offset-transparent pointer-events-none transition-all duration-200"
          style={{
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
            boxShadow: '0 0 0 9999px rgba(17, 24, 39, 0.55)',
          }}
        />
      ) : (
        <button
          type="button"
          className="absolute inset-0 bg-gray-900/55"
          aria-label="Tutorial backdrop"
          onClick={exitTour}
        />
      )}

      {targetRect && (
        <button
          type="button"
          className="absolute inset-0"
          aria-label="Tutorial backdrop"
          onClick={exitTour}
        />
      )}

      <div
        className="absolute z-[111] bg-surface rounded-lg border border-surface-border shadow-xl p-4 pointer-events-auto"
        style={cardStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tutorial-step-title"
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <p className="text-2xs font-medium uppercase tracking-wide text-primary-600">
              {activeChapter.title}
            </p>
            <h2 id="tutorial-step-title" className="text-sm font-semibold text-gray-900 mt-0.5">
              {activeStep.title}
            </h2>
          </div>
          <button
            type="button"
            onClick={exitTour}
            className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-surface-hover"
            aria-label="Exit tutorial"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="text-xs text-gray-600 leading-relaxed mb-4">
          {activeStep.body}
        </p>

        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5">
            {activeChapter.steps.map((step, index) => (
              <span
                key={step.id}
                className={clsx(
                  'h-1.5 rounded-full transition-all',
                  index === activeStepIndex
                    ? 'w-4 bg-primary-600'
                    : index < activeStepIndex
                      ? 'w-1.5 bg-primary-300'
                      : 'w-1.5 bg-gray-200'
                )}
                aria-hidden="true"
              />
            ))}
            <span className="text-2xs text-gray-400 ml-1 tabular-nums">
              {activeStepIndex + 1} / {totalSteps}
            </span>
          </div>

          <div className="flex items-center gap-1.5">
            {!isFirstStep && (
              <button
                type="button"
                onClick={back}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 border border-surface-border rounded-md hover:bg-surface-hover"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Back
              </button>
            )}
            <button
              type="button"
              onClick={next}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700"
            >
              {isLastStep ? 'Finish' : 'Next'}
              {!isLastStep && <ChevronRight className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
